import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import FloatingActionButton from "./components/ui/FloatingActionButton";
import SEO from "./components/seo";
import NotFound from "pages/NotFound";
import PropertySearchBrowse from './pages/property-search-browse';
import Login from './pages/login';
import Home from './pages/home';
import Hostels from './pages/hostels';
import Hotels from './pages/hotels';
import UserDashboard from './pages/user-dashboard';
import PropertyDetail from './pages/property-detail';
import HostelDetail from './pages/hostels/components/HostelDetail';
import HotelDetail from './pages/hotels/components/HotelDetail';
import Register from './pages/register';
import SavedPropertiesPage from './pages/saved-properties'; 
import AboutUs from './pages/about';
import OurTeam from './pages/team';
import Careers from './pages/careers';
import Press from './pages/press';
import ListProperty from './pages/list-property';
import HelpCenter from './pages/help';
import ContactUs from './pages/contact';
import PrivacyPolicy from './pages/privacy';
import TermsOfService from './pages/terms';
import PropertyGuides from './pages/guides';
import MarketReports from './pages/reports';
import InvestmentTips from './pages/tips';
import Blog from './pages/blog';
import UserProfile from './pages/user-profile'; // Import the new UserProfile page

const baseUrl = 'https://hirebroker.co';

const seoConfig = {
  '/': {
   
  title: 'Secure Real Estate Escrow & Verified Property Listings in Uganda | HireBroker',
  description: 'Eliminate agent fraud. HireBroker offers direct access to verified property listings, land plots, and rentals across Kampala, Wakiso, and Entebbe backed by centralized transactional escrow protection. List your asset instantly for a flat 10,000 UGX.',
  keywords: 'secure real estate uganda, buy land kampala safely, verified property listings uganda, escrow property transactions east africa, hirebroker kampala, land title verification wakiso, avoid real estate fraud uganda',
  canonicalUrl: `${baseUrl}/`

  },
  '/hostels': {
    title: 'Hostels for Rent in Uganda & East Africa',
    description: 'Find verified hostels and student accommodation across East Africa. Search available rooms, compare options, and book the best hostel today.',
    canonicalUrl: `${baseUrl}/hostels`
  },
  '/hotels': {
    title: 'Hotels & Hospitality Listings',
    description: 'Discover hotel rooms, safaris, staycations, and hospitality properties across Uganda and East Africa with HireBroker.',
    canonicalUrl: `${baseUrl}/hotels`
  },
  '/property-search-browse': {
    title: 'Find Properties for Sale & Rent',
    description: 'Search properties, apartments, houses, land, hostels, and hotels across Uganda and East Africa. Use powerful filters to find the right home or investment.',
    canonicalUrl: `${baseUrl}/property-search-browse`
  },
  '/login': {
    title: 'Login to HireBroker',
    description: 'Access your HireBroker account to save properties, manage searches, and stay connected with brokers and listing updates.',
    canonicalUrl: `${baseUrl}/login`
  },
  '/register': {
    title: 'Register with HireBroker',
    description: 'Create your HireBroker account to save favorites, set search alerts, and get matched with rental and land listings across East Africa.',
    canonicalUrl: `${baseUrl}/register`
  },
  '/saved-properties': {
    title: 'Saved Properties',
    description: 'View your saved properties and latest listings in one place. Manage favorites, continue browsing, and compare properties easily.',
    canonicalUrl: `${baseUrl}/saved-properties`
  },
  '/about': {
    title: 'About HireBroker',
    description: 'Learn how HireBroker is transforming real estate in East Africa with verified listings, expert brokers, and a better property search experience.',
    canonicalUrl: `${baseUrl}/about`
  },
  '/team': {
    title: 'Meet the HireBroker Team',
    description: 'Get to know the experienced team behind HireBroker, your trusted property brokers and real estate advisors in Uganda and the region.',
    canonicalUrl: `${baseUrl}/team`
  },
  '/careers': {
    title: 'Careers at HireBroker',
    description: 'Explore career opportunities at HireBroker and join a fast-growing real estate platform serving Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/careers`
  },
  '/press': {
    title: 'Press & Media | HireBroker',
    description: 'Read HireBroker press releases, media coverage, and news about our property marketplace in Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/press`
  },
  '/list-property': {
    title: 'List Your Property on HireBroker',
    description: 'Reach thousands of buyers and renters by listing your property on HireBroker. Sell, rent, or advertise land fast with expert support.',
    canonicalUrl: `${baseUrl}/list-property`
  },
  '/help': {
    title: 'HireBroker Help Center',
    description: 'Find answers to common questions, browse support resources, and get help using HireBroker’s property search tools and services.',
    canonicalUrl: `${baseUrl}/help`
  },
  '/contact': {
    title: 'Contact HireBroker',
    description: 'Contact HireBroker for property advice, landlord services, app support, and help finding rental homes, land, hostels, or hotels.',
    canonicalUrl: `${baseUrl}/contact`
  },
  '/user-dashboard': {
    title: 'My Dashboard | HireBroker',
    description: 'Manage your saved properties, search alerts, and bookings from your HireBroker dashboard.',
    canonicalUrl: `${baseUrl}/user-dashboard`
  },
  '/user-profile': {
    title: 'User Profile | HireBroker',
    description: 'Keep your HireBroker profile up to date to get accurate property recommendations and broker support.',
    canonicalUrl: `${baseUrl}/user-profile`
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'Read HireBroker’s privacy policy to understand how we collect, use, and protect your personal and property search data.',
    canonicalUrl: `${baseUrl}/privacy`
  },
  '/terms': {
    title: 'Terms & Conditions',
    description: 'Review the HireBroker terms and conditions for using our property marketplace, listings, and broker services.',
    canonicalUrl: `${baseUrl}/terms`
  },
  '/guides': {
    title: 'Real Estate Guides & Advice',
    description: 'Read expert guides on buying, renting, and investing in property across Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/guides`
  },
  '/reports': {
    title: 'Market Reports & Insights',
    description: 'Access real estate market reports, investment research, and property trends for Uganda and East Africa.',
    canonicalUrl: `${baseUrl}/reports`
  },
  '/tips': {
    title: 'Property Tips & Investment Advice',
    description: 'Get property tips, rental advice, and investment strategies for buyers and renters across East Africa.',
    canonicalUrl: `${baseUrl}/tips`
  },
  '/blog': {
    title: 'HireBroker Blog',
    description: 'Read the latest real estate news, property market updates, and expert commentary from HireBroker.',
    canonicalUrl: `${baseUrl}/blog`
  },
  '*': {
    title: 'Page Not Found | HireBroker',
    description: 'The page you are looking for could not be found. Return to HireBroker to continue searching property listings and broker services.',
    canonicalUrl: `${baseUrl}`
  }
};

const SeoLayout = ({ seo, children }) => (
  <>
    <SEO {...seo} />
    {children}
  </>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <FloatingActionButton />
        <RouterRoutes>
          {/* Define your route here */}
          <Route path="/" element={<SeoLayout seo={seoConfig['/']}><Home /></SeoLayout>} />
          <Route path="/hostels" element={<SeoLayout seo={seoConfig['/hostels']}><Hostels /></SeoLayout>} />
          <Route path="/hotels" element={<SeoLayout seo={seoConfig['/hotels']}><Hotels /></SeoLayout>} />
          <Route path="/property-search-browse" element={<SeoLayout seo={seoConfig['/property-search-browse']}><PropertySearchBrowse /></SeoLayout>} />
          <Route path="/login" element={<SeoLayout seo={seoConfig['/login']}><Login /></SeoLayout>} />
          <Route element={<ProtectedRoute />}>
            <Route path="/user-dashboard" element={<SeoLayout seo={seoConfig['/user-dashboard']}><UserDashboard /></SeoLayout>} />
            <Route path="/user-profile" element={<SeoLayout seo={seoConfig['/user-profile']}><UserProfile /></SeoLayout>} /> {/* New protected route for UserProfile */}
          </Route>
          <Route path="/property/:slug_id" element={<PropertyDetail />} />
          <Route path="/hostels/:slug_id" element={<HostelDetail />} />
          <Route path="/hotels/:slug_id" element={<HotelDetail />} />
          <Route path="/register" element={<SeoLayout seo={seoConfig['/register']}><Register /></SeoLayout>} />
          <Route path="/saved-properties" element={<SeoLayout seo={seoConfig['/saved-properties']}><SavedPropertiesPage /></SeoLayout>} />

          {/* Company Pages */}
          <Route path="/about" element={<SeoLayout seo={seoConfig['/about']}><AboutUs /></SeoLayout>} />
          <Route path="/team" element={<SeoLayout seo={seoConfig['/team']}><OurTeam /></SeoLayout>} />
          <Route path="/careers" element={<SeoLayout seo={seoConfig['/careers']}><Careers /></SeoLayout>} />
          <Route path="/press" element={<SeoLayout seo={seoConfig['/press']}><Press /></SeoLayout>} />

          {/* Services Pages */}
          <Route path="/list-property" element={<SeoLayout seo={seoConfig['/list-property']}><ListProperty /></SeoLayout>} />

          {/* Support Pages */}
          <Route path="/help" element={<SeoLayout seo={seoConfig['/help']}><HelpCenter /></SeoLayout>} />
          <Route path="/contact" element={<SeoLayout seo={seoConfig['/contact']}><ContactUs /></SeoLayout>} />
          <Route path="/privacy" element={<SeoLayout seo={seoConfig['/privacy']}><PrivacyPolicy /></SeoLayout>} />
          <Route path="/terms" element={<SeoLayout seo={seoConfig['/terms']}><TermsOfService /></SeoLayout>} />

          {/* Resources Pages */}
          <Route path="/guides" element={<SeoLayout seo={seoConfig['/guides']}><PropertyGuides /></SeoLayout>} />
          <Route path="/reports" element={<SeoLayout seo={seoConfig['/reports']}><MarketReports /></SeoLayout>} />
          <Route path="/tips" element={<SeoLayout seo={seoConfig['/tips']}><InvestmentTips /></SeoLayout>} />
          <Route path="/blog" element={<SeoLayout seo={seoConfig['/blog']}><Blog /></SeoLayout>} />

          <Route path="*" element={<SeoLayout seo={seoConfig['*']}><NotFound /></SeoLayout>} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
